import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional, REQUEST } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';
import { AppService } from '../app/app.service';
import { LangModel } from 'app/models/lang-model';
import { DEFAULT_LANGUAGE, isSupportedLanguage, SupportedLanguage } from './languages';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly langs: LangModel[] = AppService.langs;

  private current: SupportedLanguage = DEFAULT_LANGUAGE;

  constructor(
    private router: Router,
    private appService: AppService,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(REQUEST) private request: Request | null
  ) {}

  initialize(): void {
    const pathname = this.request
      ? new URL(this.request.url).pathname
      : this.document.location?.pathname ?? '/';
    this.syncFromUrl(pathname);
  }

  get currentLang(): SupportedLanguage { return this.current; }
  get currentModel(): LangModel { return this.langs.find(lang => lang.name === this.current.toUpperCase())!; }

  setCurrent(value: string | null | undefined): boolean {
    if (!isSupportedLanguage(value)) return false;
    this.current = value;
    AppService.langSel = this.currentModel;
    this.appService.selectedLanguage = this.currentModel;
    return true;
  }

  syncFromUrl(url: string): boolean {
    return this.setCurrent(url.split(/[?#]/, 1)[0].split('/')[1]);
  }

  path(path = ''): string {
    return `/${this.current}${path ? '/' + path.replace(/^\/+/, '') : ''}`;
  }

  localizeUrl(url: string): string {
    if (/^(?:[a-z][a-z\d+.-]*:|\/\/|#)/i.test(url)) return url;
    if (/^\/r\/[^/?#]+/.test(url)) return url;
    const tree = this.router.parseUrl(url.startsWith('/') ? url : '/' + url);
    const group = tree.root.children['primary'];
    if (!group) return this.path() + (url.match(/[?#].*$/)?.[0] ?? '');
    const segments = group.segments;
    group.segments = segments.length && isSupportedLanguage(segments[0].path)
      ? [new UrlSegment(this.current, segments[0].parameters), ...segments.slice(1)]
      : [new UrlSegment(this.current, {}), ...segments];
    return this.router.serializeUrl(tree);
  }

  switchTo(value: string): Promise<boolean> {
    if (!isSupportedLanguage(value)) return Promise.resolve(false);
    const tree = this.router.parseUrl(this.router.url);
    const group = tree.root.children['primary'];
    const segments = group?.segments;
    if (segments?.length && isSupportedLanguage(segments[0].path)) {
      group.segments = [new UrlSegment(value, segments[0].parameters), ...segments.slice(1)];
    } else {
      return this.router.navigateByUrl(`/${value}`);
    }
    return this.router.navigateByUrl(tree);
  }
}
